import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';

const OrderTracking = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrder = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchOrder();
    const interval = setInterval(fetchOrder, 10000);
    return () => clearInterval(interval);
  }, [id]);

  // ✅ Función para normalizar la URL del comprobante
  const normalizeUrl = (url) => {
    if (!url) return null;
    
    // Si ya es una URL válida
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Si viene mal formada con railway.apphttps
    if (url.includes('railway.apphttps')) {
      const match = url.match(/(https?:\/\/i\.ibb\.co\/[^\s]+)/);
      if (match) return match[1];
      // Extraer solo la parte de imgbb
      const imgbbMatch = url.match(/(i\.ibb\.co\/[^\s]+)/);
      if (imgbbMatch) return `https://${imgbbMatch[1]}`;
    }
    
    // Si empieza con https// (sin :)
    if (url.startsWith('https//')) {
      return url.replace('https//', 'https://');
    }
    
    // Si empieza con /uploads
    if (url.startsWith('/uploads')) {
      return `https://tuupastel-production.up.railway.app${url}`;
    }
    
    return url;
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-gray-500">Cargando pedido...</p>
      </div>
    </div>
  );

  if (!order) return (
    <div className="text-center py-20">
      <div className="text-6xl mb-4">😕</div>
      <h2 className="text-2xl font-bold text-gray-700 mb-2">Pedido no encontrado</h2>
      <Link to="/" className="text-gold hover:underline">Volver al inicio</Link>
    </div>
  );

  const statusSteps = [
    { key: 'pendiente', label: 'Recibido', icon: '📋' },
    { key: 'aceptado', label: 'Confirmado', icon: '✅' },
    { key: 'preparando', label: 'Preparando', icon: '👨‍🍳' },
    { key: 'en_camino', label: 'En camino', icon: '🚗' },
    { key: 'entregado', label: 'Entregado', icon: '🎉' }
  ];

  const currentStep = statusSteps.findIndex(s => s.key === order.status);

  const isPendingPayment = order.payment_status === 'pagado';
  const isPaymentRejected = order.payment_status === 'fallido';
  const isPaymentConfirmed = order.payment_status === 'confirmado';

  // ✅ Obtener datos del motorizado
  const driver = order.driver || order.delivery?.driver || null;
  const driverName = driver?.name || order.driver_name || null;
  const driverPhone = driver?.phone || order.driver_phone || null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/historial" className="text-gold text-sm hover:underline">← Volver al historial</Link>
        <h1 className="text-2xl md:text-3xl font-serif font-bold mt-2">Pedido #{order.order_number}</h1>
        <p className="text-gray-500 text-sm">
          {new Date(order.created_at).toLocaleString('es-VE')}
        </p>
      </div>

      {isPendingPayment && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-xl mb-6">
          <div className="flex items-center gap-3">
            <div className="text-3xl">⏳</div>
            <div>
              <p className="font-semibold text-yellow-800">Estamos verificando tu pago</p>
              <p className="text-sm text-yellow-700">
                Tu comprobante está en revisión. Te notificaremos cuando sea aprobado.
              </p>
            </div>
          </div>
        </div>
      )}

      {isPaymentConfirmed && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-xl mb-6">
          <div className="flex items-center gap-3">
            <div className="text-3xl">✅</div>
            <div>
              <p className="font-semibold text-green-800">¡Pago confirmado!</p>
              <p className="text-sm text-green-700">
                {order.status === 'entregado' ? 'Tu pedido fue entregado' : 'Estamos preparando tu pedido.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {isPaymentRejected && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl mb-6">
          <div className="flex items-center gap-3">
            <div className="text-3xl">❌</div>
            <div>
              <p className="font-semibold text-red-800">Pago rechazado</p>
              <p className="text-sm text-red-700">
                {order.payment_data?.rejectionReason || 'Verifica los datos e intenta nuevamente'}
              </p>
            </div>
          </div>
        </div>
      )}

      {!isPaymentRejected && !isPendingPayment && (
        <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex justify-between relative">
            {statusSteps.map((step, index) => (
              <div key={step.key} className="flex flex-col items-center flex-1">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-base md:text-xl border-2 transition-all ${
                  index <= currentStep
                    ? 'bg-gold border-gold text-white shadow-lg'
                    : 'bg-white border-gray-200 text-gray-400'
                }`}>
                  {step.icon}
                </div>
                <span className={`text-[10px] md:text-xs mt-2 text-center font-medium ${
                  index <= currentStep ? 'text-primary' : 'text-gray-400'
                }`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-lg mb-3">📍 Entrega</h3>
          <div className="space-y-2 text-sm">
            <p><span className="text-gray-500">Nombre:</span> {order.customer_info?.name}</p>
            <p><span className="text-gray-500">Teléfono:</span> {order.customer_info?.phone}</p>
            <p><span className="text-gray-500">Dirección:</span> {order.customer_info?.address}</p>
            {order.delivery_location?.lat && (
              <a
                href={`https://www.google.com/maps?q=${order.delivery_location.lat},${order.delivery_location.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-blue-600 hover:underline text-xs"
              >
                📍 Ver ubicación en Google Maps →
              </a>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-lg mb-3">🚗 Motorizado</h3>
          {driverName ? (
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center text-white font-bold">
                {driverName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium">{driverName}</p>
                {driverPhone && <p className="text-sm text-gray-500">{driverPhone}</p>}
              </div>
            </div>
          ) : order.status === 'entregado' ? (
            <p className="text-gray-500 text-sm">✅ Entregado por nuestro equipo</p>
          ) : (
            <p className="text-gray-500 text-sm">Asignando motorizado...</p>
          )}
        </div>

        <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100 md:col-span-2">
          <h3 className="font-semibold text-lg mb-3">🛒 Productos</h3>
          {order.items?.map((item, i) => (
            <div key={i} className="flex justify-between text-sm border-b border-gray-100 py-2 last:border-0">
              <span>{item.quantity}x {item.name}</span>
              <span className="font-medium">${(Number(item.priceUSD) * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="mt-3 pt-3 border-t-2 border-dashed">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total</span>
              <div className="text-right">
                <div className="text-xl font-bold text-gold">${Number(order.total_usd).toFixed(2)}</div>
                <div className="text-xs text-gray-500">Bs {Number(order.total_bs).toFixed(2)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Comprobante con URL normalizada */}
        {order.payment_data?.comprobante && (
          <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100 md:col-span-2">
            <h3 className="font-semibold text-lg mb-3">💳 Comprobante</h3>
            <img
              src={normalizeUrl(order.payment_data.comprobante)}
              alt="Comprobante"
              className="max-h-64 rounded-xl mx-auto border border-gray-200"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.insertAdjacentHTML('beforeend',
                  '<p class="text-center text-sm text-gray-500 mt-2">No se pudo cargar el comprobante</p>'
                );
              }}
            />
          </div>
        )}

        {/* Foto de entrega */}
        {order.delivery_photo && (
          <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100 md:col-span-2">
            <h3 className="font-semibold text-lg mb-3">📸 Foto de entrega</h3>
            <img
              src={normalizeUrl(order.delivery_photo)}
              alt="Entrega"
              className="max-h-64 rounded-xl mx-auto border border-gray-200"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
